// src/components/Admin/AdminDashboard.jsx
import React, { useState, useEffect, useRef } from 'react';
import { 
  Menu, 
  LayoutDashboard, 
  Users, 
  Building2, 
  LogOut, 
  Check, 
  X, 
  ExternalLink,
  ChevronDown,
  ShieldCheck,
  CreditCard,
  Award,
  Clock
} from 'lucide-react';
import './AdminDashboard.css';
import MembersTable from './MembersTable';
import SuppliersTable from './SuppliersTable';

export default function AdminDashboardd({ currentAdmin, onLogout }) {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [analytics, setAnalytics] = useState(null);
  const [members, setMembers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  
  // Navigation & Dropdown states
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false); // Large devices (text toggle)
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false); // Small devices (drawer toggle)
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const dropdownRef = useRef(null);

  // Close profile dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch metrics & datasets
  useEffect(() => {
    fetchAnalytics();
    if (activeTab === 'members') fetchMembers();
    if (activeTab === 'suppliers') fetchSuppliers();
  }, [activeTab]);

  const fetchAnalytics = async () => {
    try {
      const res = await fetch('https://api.lbnhyderabad.com/api/admin/analytics');
      const json = await res.json();
      if (json.success) setAnalytics(json.data);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchMembers = async () => {
    try {
      const res = await fetch('https://api.lbnhyderabad.com/api/admin/members');
      const json = await res.json();
      if (json.success) setMembers(json.data);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const res = await fetch('https://api.lbnhyderabad.com/api/admin/suppliers');
      const json = await res.json();
      if (json.success) setSuppliers(json.data);
    } catch (e) {
      console.error(e);
    }
  };


const updateMemberStatus = async (id, status) => {
  try {
    const res = await fetch(`https://api.lbnhyderabad.com/api/admin/members/${id}/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status })
    });
    
    const data = await res.json();

    if (res.ok && data.success) {
      // If approved, update status AND assign the newly generated privilege_code
      setMembers(prevMembers =>
        prevMembers.map(m => {
          if (m.id === id) {
            return {
              ...m,
              status,
              privilege_code: data.privilege_code || m.privilege_code
            };
          }
          return m;
        })
      );

      // Refresh dashboard analytics counters
      fetchAnalytics();
    } else {
      alert(data.message || 'Failed to update member status');
    }
  } catch (e) {
    console.error('Error updating member status:', e);
    alert('Server error updating member status');
  }
};

  const updateSupplierStatus = async (id, status) => {
    try {
      const res = await fetch(`https://api.lbnhyderabad.com/api/admin/suppliers/${id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      if (res.ok) {
        setSuppliers(suppliers.map(s => s.id === id ? { ...s, status } : s));
        fetchAnalytics();
      }
    } catch (e) {
      console.error(e);
    }
  };

  // Toggle handler for both large screens and small screens
  const handleHamburgerClick = () => {
    if (window.innerWidth <= 868) {
      setMobileDrawerOpen(!mobileDrawerOpen);
    } else {
      setSidebarCollapsed(!sidebarCollapsed);
    }
  };

  const handleTabSelect = (tab) => {
    setActiveTab(tab);
    if (window.innerWidth <= 868) {
      setMobileDrawerOpen(false); // Close drawer on mobile after selection
    }
  };

  // Search filters
  const filteredMembers = members.filter(m =>
    m.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.membership_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.district?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredSuppliers = suppliers.filter(s =>
    s.company_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.gst_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.core_offering_category?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="lux-admin-shell">
      {/* 1. Backdrop Overlay for Mobile Drawer */}
      <div 
        className={`mobile-drawer-backdrop ${mobileDrawerOpen ? 'active' : ''}`}
        onClick={() => setMobileDrawerOpen(false)}
      />

      {/* 2. Responsive Sidebar (Mini Rail on Desktop, Drawer on Mobile) */}
      <aside 
        className={`lux-sidebar ${sidebarCollapsed ? 'collapsed' : ''} ${mobileDrawerOpen ? 'mobile-open' : ''}`}
      >
        <div className="sidebar-header-box">
          <div className="sidebar-gold-emblem">
            <Award size={20} />
          </div>
          <div className="sidebar-brand-text">
            <span className="brand-main-title">Privilege</span>
            <span className="brand-sub-tag">EXECUTIVE</span>
          </div>
        </div>

        <nav className="sidebar-nav-list">
          <button 
            className={`sidebar-nav-item ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => handleTabSelect('dashboard')}
            title="Dashboard & Analytics"
          >
            <div className="nav-icon"><LayoutDashboard size={18} /></div>
            <span>Dashboard & Insights</span>
          </button>

          <button 
            className={`sidebar-nav-item ${activeTab === 'members' ? 'active' : ''}`}
            onClick={() => handleTabSelect('members')}
            title="Members Roster"
          >
            <div className="nav-icon"><Users size={18} /></div>
            <span>Members Directory</span>
          </button>

          <button 
            className={`sidebar-nav-item ${activeTab === 'suppliers' ? 'active' : ''}`}
            onClick={() => handleTabSelect('suppliers')}
            title="Suppliers Network"
          >
            <div className="nav-icon"><Building2 size={18} /></div>
            <span>Suppliers Network</span>
          </button>
        </nav>

        <div className="sidebar-footer-pane">
          <button onClick={onLogout} className="sidebar-mini-logout" title="Sign Out">
            <LogOut size={16} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* 3. Main Stage Container */}
      <div className={`lux-main-stage ${sidebarCollapsed ? 'expanded' : ''}`}>
        {/* Executive Top Header */}
      <header className="lux-admin-header">
  <div className="header-left-actions">
    <button 
      className="btn-hamburger-toggle" 
      onClick={handleHamburgerClick}
      title="Toggle Navigation"
    >
      <Menu size={18} />
    </button>

    <div className="header-page-title">
      <h2>
        {activeTab === 'dashboard' && 'Dashboard Overview'}
        {activeTab === 'members' && 'Member Directory'}
        {activeTab === 'suppliers' && 'Suppliers Network'}
      </h2>
      <span>Executive Management Control</span>
    </div>
  </div>

  {/* Profile Dropdown */}
  <div className="header-right-profile" ref={dropdownRef}>
    <button 
      className="profile-chip-btn"
      onClick={() => setDropdownOpen(!dropdownOpen)}
      aria-label="Admin Profile Menu"
    >
      <div className="profile-chip-avatar">
        {currentAdmin?.email ? currentAdmin.email.charAt(0).toUpperCase() : 'A'}
      </div>
      <div className="profile-chip-meta">
        <span className="profile-chip-role">Admin Account</span>
        <span className="profile-chip-name">{currentAdmin?.email || 'admin@privilegecard.com'}</span>
      </div>
      <ChevronDown size={14} color="#00529b" />
    </button>

    {dropdownOpen && (
      <div className="admin-profile-popover">
        <div className="popover-header">
          <span>Signed in administrator</span>
          <strong>{currentAdmin?.email || 'admin@privilegecard.com'}</strong>
        </div>
        <button onClick={onLogout} className="popover-logout-btn">
          <LogOut size={14} />
          <span>Sign Out</span>
        </button>
      </div>
    )}
  </div>
</header>

        {/* Stage Content */}
        <div className="stage-scroll-body">
          {/* TAB 1: DASHBOARD & INSIGHTS */}
{activeTab === 'dashboard' && (
  <>
    {/* 4 Focused KPI Cards: Members & Suppliers */}
    <div className="kpi-master-grid">
      {/* 1. Approved Members */}
      <div className="kpi-feature-card">
        <div className="kpi-accent-stripe" />
        <div className="kpi-header-row">
          <span className="kpi-title-label">Approved Members</span>
          <div className="kpi-icon-pill"><Users size={16} color="#166534" /></div>
        </div>
        <div className="kpi-number-display" style={{ color: '#166534' }}>
          {analytics?.members?.approved || 0}
        </div>
        <div className="kpi-footer-status">
          <span>Total Registered</span>
          <span className="status-badge-green">{analytics?.members?.total || 0} Total</span>
        </div>
      </div>

      {/* 2. Pending Members */}
      <div className="kpi-feature-card">
        <div className="kpi-accent-stripe" />
        <div className="kpi-header-row">
          <span className="kpi-title-label">Pending Members</span>
          <div className="kpi-icon-pill"><Clock size={16} color="#b45309" /></div>
        </div>
        <div className="kpi-number-display" style={{ color: '#b45309' }}>
          {analytics?.members?.pending || 0}
        </div>
        <div className="kpi-footer-status">
          <span>Awaiting Review</span>
          <span className="status-badge-gold">Action Required</span>
        </div>
      </div>

      {/* 3. Approved Suppliers */}
      <div className="kpi-feature-card">
        <div className="kpi-accent-stripe" />
        <div className="kpi-header-row">
          <span className="kpi-title-label">Approved Suppliers</span>
          <div className="kpi-icon-pill"><Building2 size={16} color="#00529b" /></div>
        </div>
        <div className="kpi-number-display" style={{ color: '#00529b' }}>
          {analytics?.suppliers?.approved || 0}
        </div>
        <div className="kpi-footer-status">
          <span>Active Alliances</span>
          <span className="status-badge-green">{analytics?.suppliers?.total || 0} Total</span>
        </div>
      </div>

      {/* 4. Pending Suppliers */}
      <div className="kpi-feature-card">
        <div className="kpi-accent-stripe" />
        <div className="kpi-header-row">
          <span className="kpi-title-label">Pending Suppliers</span>
          <div className="kpi-icon-pill"><Clock size={16} color="#b45309" /></div>
        </div>
        <div className="kpi-number-display" style={{ color: '#b45309' }}>
          {analytics?.suppliers?.pending || 0}
        </div>
        <div className="kpi-footer-status">
          <span>Under Verification</span>
          <span className="status-badge-gold">Review Needed</span>
        </div>
      </div>
    </div>

    {/* Analytical Overview: Member Approvals vs District Quotas */}
    <div className="analytics-hero-deck">
      {/* Visual Donut Chart: Member Status Distribution */}
      <div className="luxury-chart-container">
        <div className="chart-header-row">
          <h3 className="chart-header-title">Member Verification Status</h3>
          <span className="badge-tag-gold">Approval Ratio</span>
        </div>

        {(() => {
          const total = analytics?.members?.total || 1;
          const approved = analytics?.members?.approved || 0;
          const pending = analytics?.members?.pending || 0;
          const rejected = analytics?.members?.rejected || 0;

          const circumference = 289;
          const approvedDash = (approved / total) * circumference;
          const pendingDash = (pending / total) * circumference;
          const rejectedDash = (rejected / total) * circumference;

          return (
            <div className="donut-chart-layout">
              <div className="donut-svg-wrapper">
                <svg viewBox="0 0 110 110" className="donut-svg" width="130" height="130">
                  <circle cx="55" cy="55" r="46" fill="transparent" stroke="#f1f5f9" strokeWidth="12" />
                  <circle
                    cx="55"
                    cy="55"
                    r="46"
                    fill="transparent"
                    stroke="#00529b"
                    strokeWidth="12"
                    strokeDasharray={`${approvedDash} ${circumference}`}
                    strokeDashoffset="0"
                  />
                  <circle
                    cx="55"
                    cy="55"
                    r="46"
                    fill="transparent"
                    stroke="#febf2c"
                    strokeWidth="12"
                    strokeDasharray={`${pendingDash} ${circumference}`}
                    strokeDashoffset={-approvedDash}
                  />
                  <circle
                    cx="55"
                    cy="55"
                    r="46"
                    fill="transparent"
                    stroke="#ef4444"
                    strokeWidth="12"
                    strokeDasharray={`${rejectedDash} ${circumference}`}
                    strokeDashoffset={-(approvedDash + pendingDash)}
                  />
                </svg>

                <div className="donut-center-label">
                  <div className="donut-center-number">{analytics?.members?.total || 0}</div>
                  <div className="donut-center-sub">Members</div>
                </div>
              </div>

              <div className="donut-legend-stack">
                <div className="legend-row-item">
                  <span><span className="legend-color-dot" style={{ background: '#00529b' }} /> Approved</span>
                  <span className="legend-val-num">{approved} ({Math.round((approved / total) * 100)}%)</span>
                </div>
                <div className="legend-row-item">
                  <span><span className="legend-color-dot" style={{ background: '#febf2c' }} /> Pending</span>
                  <span className="legend-val-num">{pending} ({Math.round((pending / total) * 100)}%)</span>
                </div>
                <div className="legend-row-item">
                  <span><span className="legend-color-dot" style={{ background: '#ef4444' }} /> Rejected</span>
                  <span className="legend-val-num">{rejected} ({Math.round((rejected / total) * 100)}%)</span>
                </div>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Regional District Allocation */}
      <div className="luxury-chart-container">
        <div className="chart-header-row">
          <h3 className="chart-header-title">District Enrollment</h3>
          <span className="badge-tag-gold">Districts 320A - 320H</span>
        </div>

        <div className="district-bars-vertical">
          {analytics?.districtStats && analytics.districtStats.length > 0 ? (
            analytics.districtStats.map(stat => (
              <div className="district-meter-row" key={stat.district}>
                <span className="meter-dist-title">District {stat.district}</span>
                <div className="meter-track-bg">
                  <div
                    className="meter-fill-bar"
                    style={{
                      width: `${Math.min(100, (stat.count / (analytics.members?.total || 1)) * 100)}%`
                    }}
                  />
                </div>
                <span className="meter-count-label">{stat.count}</span>
              </div>
            ))
          ) : (
            <div style={{ color: '#94a3b8', fontSize: '0.8rem', padding: '16px' }}>
              No regional members recorded.
            </div>
          )}
        </div>
      </div>
    </div>

    {/* Supplier Sector Penetration */}
    <div className="luxury-chart-container" style={{ marginBottom: '22px' }}>
      <div className="chart-header-row">
        <h3 className="chart-header-title">Active Supplier Sectors</h3>
        <span className="badge-tag-gold">Industry Share</span>
      </div>

      <div className="sector-list-stack">
        {analytics?.categoryStats && analytics.categoryStats.length > 0 ? (
          analytics.categoryStats.map(cat => (
            <div className="sector-item-box" key={cat.core_offering_category}>
              <div className="sector-meta-row">
                <span>{cat.core_offering_category}</span>
                <strong>{cat.count} Suppliers</strong>
              </div>
              <div className="sector-bar-line">
                <div
                  className="sector-fill-gold"
                  style={{
                    width: `${Math.min(100, (cat.count / (analytics.suppliers?.total || 1)) * 100)}%`
                  }}
                />
              </div>
            </div>
          ))
        ) : (
          <div style={{ color: '#94a3b8', fontSize: '0.8rem', padding: '16px' }}>
            No supplier categories registered.
          </div>
        )}
      </div>
    </div>
  </>
)}
        {activeTab === 'members' && (
            <MembersTable 
              members={members} 
              onUpdateStatus={updateMemberStatus} 
            />
          )}

          {/* TAB 3: SEPARATE COMPONENT */}
          {activeTab === 'suppliers' && (
            <SuppliersTable 
              suppliers={suppliers} 
              onUpdateStatus={updateSupplierStatus} 
            />
          )}
        </div>
      </div>
    </div>
  );
}